import { useMemo } from "react";

import { TiVoTargets } from "hooks/tivoApps";
import {
  TiVoDeviceOem,
  TiVoDevicePlatform,
  usePlatforms,
} from "hooks/tivoDevices";

// Helper to filter group values only if at least one id from restrictIds is present in the group
function filterGroupsByRestriction<T>(items: T[], restrictIds?: string[]): T[] {
  if (!restrictIds || restrictIds.length === 0) {
    return items;
  }
  const getId = (item: any) => item.id ?? item;
  const filtered = items.filter((item) => restrictIds.includes(getId(item)));
  return filtered.length ? filtered : items;
}

function filterObj<T extends TiVoDevicePlatform | TiVoDeviceOem>(
  input: T[] = [],
  ids: string[] = []
): T[] {
  return ids.length ? input.filter((p) => ids.includes(p.id)) : input;
}

export type TiVoDeviceSource = {
  getOemIds: (
    platformIds?: string[],
    restrictTargets?: TiVoTargets
  ) => string[];
  getOems: (
    platformIds?: string[],
    restrictTargets?: TiVoTargets
  ) => Record<string, string[]>;
  getClientTypeIds: (
    platformIds?: string[],
    oemIds?: string[],
    restrictTargets?: TiVoTargets
  ) => string[];
  getClientTypes: (
    platformIds?: string[],
    oemIds?: string[],
    restrictTargets?: TiVoTargets
  ) => Record<string, string[]>;
  getDeviceLabel: (deviceId: string) => string;
  getDeviceSlug: (deviceId: string) => string;
  getDevicesBySlug: (deviceSlug: string) => string[];
  getOemsRequiringConfirmation: (targets?: TiVoTargets) => string[];
  getPlatformType: (deviceId: string) => string;
};

export function useTiVoDeviceData(): TiVoDeviceSource {
  const { data } = usePlatforms({ include: ["oems", "oems.clientTypes"] });
  const platforms = useMemo(() => data?.results ?? [], [data]);

  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    platforms?.forEach((platform) => {
      map.set(platform.id, platform.name);
      platform.oems?.forEach((oem) => {
        map.set(oem.id, oem.name);
        oem.clientTypes?.forEach((client) => {
          map.set(client.id, client.name);
        });
      });
    });
    return map;
  }, [platforms]);

  const platformTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    platforms?.forEach((platform) => {
      map.set(platform.id, platform.platformType);
      platform.oems?.forEach((oem) => {
        map.set(oem.id, platform.platformType);
        oem.clientTypes?.forEach((client) => {
          map.set(client.id, platform.platformType);
        });
      });
    });
    return map;
  }, [platforms]);

  const slugMap = useMemo(() => {
    const map = new Map<string, string>();
    platforms?.forEach((platform) => {
      map.set(platform.id, platform.slug || platform.id);
      platform.oems?.forEach((oem) => {
        map.set(oem.id, oem.slug || oem.id);
        oem.clientTypes?.forEach((client) => {
          map.set(client.id, client.slug || client.id);
        });
      });
    });
    return map;
  }, [platforms]);

  const slugDeviceIndex: Map<string, Set<string>> = useMemo(() => {
    const map: Map<string, Set<string>> = new Map();
    for (const [deviceId, slug] of slugMap.entries()) {
      const deviceIdSet = map.get(slug);
      if (!deviceIdSet) {
        map.set(slug, new Set([deviceId]));
        continue;
      }
      deviceIdSet.add(deviceId);
    }
    return map;
  }, [slugMap]);

  const getFilteredPlatforms = (
    platformIds?: string[],
    restrictTargets?: TiVoTargets
  ) =>
    filterObj(
      platformIds?.length
        ? platforms.filter((p) => platformIds.includes(p.id))
        : platforms,
      restrictTargets?.platforms
    );

  return {
    getOemIds: (platformIds?: string[], restrictTargets?: TiVoTargets) =>
      getFilteredPlatforms(platformIds, restrictTargets).flatMap(
        (p) =>
          filterGroupsByRestriction(p.oems || [], restrictTargets?.oems)?.map(
            (oem) => oem.id
          ) ?? []
      ),
    getOems: (platformIds?: string[], restrictTargets?: TiVoTargets) =>
      getFilteredPlatforms(platformIds, restrictTargets).reduce(
        (result: Record<string, string[]>, platform) => {
          result[platform.id] = filterGroupsByRestriction(
            platform.oems?.map((oem) => oem.id) || [],
            restrictTargets?.oems
          );
          return result;
        },
        {}
      ),

    getClientTypeIds: (
      platformIds?: string[],
      oemIds?: string[],
      restrictTargets?: TiVoTargets
    ) =>
      getFilteredPlatforms(platformIds, restrictTargets).flatMap((platform) =>
        filterObj(
          filterGroupsByRestriction(platform.oems || [], restrictTargets?.oems),
          oemIds
        ).flatMap((oem) =>
          filterGroupsByRestriction(
            oem.clientTypes?.map((client) => client.id) || [],
            restrictTargets?.clientTypes
          )
        )
      ),
    getClientTypes: (
      platformIds?: string[],
      oemIds?: string[],
      restrictTargets?: TiVoTargets
    ): Record<string, string[]> => {
      const oems = getFilteredPlatforms(platformIds, restrictTargets).flatMap(
        (platform) =>
          filterObj(
            filterGroupsByRestriction(
              platform.oems || [],
              restrictTargets?.oems
            ),
            oemIds
          )
      );
      return oems.reduce((result: Record<string, string[]>, oem) => {
        result[oem.id] = filterGroupsByRestriction(
          oem.clientTypes?.map((client) => client.id) || [],
          restrictTargets?.clientTypes
        );
        return result;
      }, {});
    },
    getDeviceLabel: (deviceId) => labelMap.get(deviceId) || deviceId,
    getDeviceSlug: (deviceId) => slugMap.get(deviceId) || deviceId,
    getDevicesBySlug: (deviceSlug) =>
      Array.from(slugDeviceIndex.get(deviceSlug) || []),
    getOemsRequiringConfirmation: (targets?: TiVoTargets): string[] =>
      getFilteredPlatforms(undefined, targets)
        .flatMap((p) => filterGroupsByRestriction(p.oems ?? [], targets?.oems))
        .filter((oem) => oem.publishingConfirmationRequired === "yes")
        .map((oem) => oem.id),
    getPlatformType: (deviceId) => platformTypeMap.get(deviceId) || "",
  };
}
